import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { CATEGORIES, CATEGORY_GROUPS } from '../data/categories';

/**
 * CategoryBrowser — fullscreen modal sheet listing the entire NoFee category
 * taxonomy with search + group navigation. Premium black/gold, mobile-first.
 *
 * Trigger via `<button onClick={() => setOpen(true)}>View All</button>`.
 */
const CategoryBrowser = ({ open, onClose }) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setQuery('');
    } else {
      document.body.style.overflow = '';
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return CATEGORIES.filter(
      (c) => c.label.toLowerCase().includes(q) || c.aliases.some((a) => a.includes(q)),
    );
  }, [query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md animate-fade-in"
      onClick={onClose}
      data-testid="category-browser"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:w-[640px] lg:w-[860px] max-h-[92vh] sm:max-h-[85vh] bg-gradient-to-b from-zinc-950 to-black border border-white/10 sm:rounded-3xl flex flex-col overflow-hidden shadow-[0_30px_80px_-20px_rgba(212,175,55,0.25)]"
      >
        {/* Header */}
        <div className="px-5 sm:px-7 pt-5 pb-4 border-b border-white/[0.06] flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                Browse all
              </p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                Food categories
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-white/55 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="category-browser-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories…"
              className="w-full pl-11 pr-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none text-[14px]"
              data-testid="category-browser-search"
              autoFocus
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-7">
          {filtered ? (
            filtered.length === 0 ? (
              <div className="text-center py-12 text-white/40 text-sm">
                No categories matching "{query}".
              </div>
            ) : (
              <CategoryGrid items={filtered} onClick={onClose} />
            )
          ) : (
            CATEGORY_GROUPS.map((group) => {
              const items = CATEGORIES.filter((c) => c.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group} data-testid={`category-group-${group.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <h3 className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">
                    {group}
                  </h3>
                  <CategoryGrid items={items} onClick={onClose} />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryGrid = ({ items, onClick }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
    {items.map((c) => (
      <Link
        key={c.id}
        to={`/category/${c.id}`}
        onClick={onClick}
        data-testid={`category-tile-${c.id}`}
        className="group flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-gold-500/40 transition-all duration-200"
      >
        <span className="text-xl flex-shrink-0">{c.emoji}</span>
        <span className="text-[13.5px] font-bold text-white group-hover:text-gold-200 truncate">
          {c.label}
        </span>
      </Link>
    ))}
  </div>
);

export default CategoryBrowser;
